import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';

const THUMBNAIL_CAP = 9;
const SWIPE_THRESHOLD = 50;

const hasText = (value) => (
	value !== null &&
	value !== undefined &&
	String(value).trim() !== ''
);

const getFileName = (slide, index) => {
	if (hasText(slide?.file_name)) {
		return String(slide.file_name).trim();
	}

	if (hasText(slide?.alt)) {
		return String(slide.alt).trim();
	}

	return `Image ${index + 1}`;
};

const getImageAlt = (slide, index, objectTitle) => {
	if (hasText(slide?.alt)) {
		return String(slide.alt).trim();
	}

	if (hasText(objectTitle)) {
		return `${objectTitle}, image ${index + 1}`;
	}

	return `Image ${index + 1}`;
};

const getDownloadUrl = (slide) => {
	if (hasText(slide?.download_url)) {
		return String(slide.download_url).trim();
	}

	if (hasText(slide?.src)) {
		return String(slide.src).trim();
	}

	return '';
};

const getThumbnailUrl = (slide) => {
	if (hasText(slide?.thumb)) {
		return String(slide.thumb).trim();
	}

	/*
	 * Do not fall back to slide.src here. The original files can be tens
	 * of megabytes and must only be requested after an explicit full-size
	 * image action.
	 */
	return '';
};

const copyToClipboard = async (text) => {
	if (
		typeof navigator !== 'undefined' &&
		navigator.clipboard &&
		typeof navigator.clipboard.writeText === 'function'
	) {
		try {
			await navigator.clipboard.writeText(text);
			return;
		} catch (error) {
			/*
			 * Clipboard access can be unavailable on non-HTTPS preview
			 * domains. Fall back to the older copy command below.
			 */
		}
	}

	const textArea = document.createElement('textarea');
	textArea.value = text;
	textArea.setAttribute('readonly', '');
	textArea.style.position = 'fixed';
	textArea.style.left = '-9999px';
	textArea.style.opacity = '0';

	document.body.appendChild(textArea);
	textArea.select();

	const copied = document.execCommand('copy');
	document.body.removeChild(textArea);

	if (!copied) {
		throw new Error('The reference could not be copied.');
	}
};

const MyGallery = ({
	slides = [],
	referenceId,
	objectTitle,
	variant = 'standard',
}) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [showAll, setShowAll] = useState(false);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(0);
	const [copyState, setCopyState] = useState('idle');

	const [leadImageState, setLeadImageState] = useState('loading');
	const [lightboxImageState, setLightboxImageState] = useState('loading');
	const [leadRetryKey, setLeadRetryKey] = useState(0);
	const [lightboxRetryKey, setLightboxRetryKey] = useState(0);

	const isHero = variant === 'hero';

	const galleryRef = useRef(null);
	const lightboxRef = useRef(null);
	const closeButtonRef = useRef(null);
	const previouslyFocusedRef = useRef(null);
	const touchStartRef = useRef(null);

	const imageSlides = useMemo(
		() => slides.filter((slide) => hasText(slide?.src)),
		[slides]
	);

	const displayedSlides = showAll
		? imageSlides
		: imageSlides.slice(0, THUMBNAIL_CAP);

	const selectedSlide = imageSlides[selectedIndex] || imageSlides[0];
	const lightboxSlide = imageSlides[lightboxIndex] || imageSlides[0];

	const updateImageHash = useCallback((index) => {
		const nextHash = `#img-${index + 1}`;

		if (window.location.hash === nextHash) {
			return;
		}

		const nextUrl = (
			`${window.location.pathname}${window.location.search}${nextHash}`
		);

		window.history.replaceState(
			window.history.state,
			'',
			nextUrl
		);
	}, []);

	useEffect(() => {
		setLeadImageState(
			hasText(selectedSlide?.thumb) ? 'loading' : 'unavailable'
		);
		setLeadRetryKey(0);
	}, [selectedSlide?.thumb]);

	useEffect(() => {
		if (lightboxOpen) {
			setLightboxImageState('loading');
			setLightboxRetryKey(0);
		}
	}, [lightboxOpen, lightboxSlide?.src]);

	const selectImage = useCallback((index) => {
		if (imageSlides.length === 0) {
			return;
		}

		const normalisedIndex = (
			(index % imageSlides.length) + imageSlides.length
		) % imageSlides.length;

		setSelectedIndex(normalisedIndex);

		if (normalisedIndex >= THUMBNAIL_CAP) {
			setShowAll(true);
		}
	}, [imageSlides.length]);

	const openLightbox = useCallback((index) => {
		if (imageSlides.length === 0) {
			return;
		}

		const normalisedIndex = (
			(index % imageSlides.length) + imageSlides.length
		) % imageSlides.length;

		previouslyFocusedRef.current = document.activeElement;

		setSelectedIndex(normalisedIndex);
		setLightboxIndex(normalisedIndex);
		setCopyState('idle');
		setLightboxImageState('loading');
		setLightboxOpen(true);

		updateImageHash(normalisedIndex);

		if (normalisedIndex >= THUMBNAIL_CAP) {
			setShowAll(true);
		}
	}, [
		imageSlides.length,
		updateImageHash,
	]);

	const closeLightbox = useCallback(() => {
		setLightboxOpen(false);

		window.setTimeout(() => {
			if (
				previouslyFocusedRef.current &&
				typeof previouslyFocusedRef.current.focus === 'function'
			) {
				previouslyFocusedRef.current.focus();
			}
		}, 0);
	}, []);

	const moveLightbox = useCallback((direction) => {
		if (imageSlides.length < 2) {
			return;
		}

		setLightboxIndex((currentIndex) => {
			const nextIndex = (
				currentIndex + direction + imageSlides.length
			) % imageSlides.length;

			setSelectedIndex(nextIndex);
			setCopyState('idle');
			setLightboxImageState('loading');
			updateImageHash(nextIndex);

			if (nextIndex >= THUMBNAIL_CAP) {
				setShowAll(true);
			}

			return nextIndex;
		});
	}, [
		imageSlides.length,
		updateImageHash,
	]);

	const retryLeadImage = () => {
		setLeadImageState('loading');
		setLeadRetryKey((currentKey) => currentKey + 1);
	};

	const retryLightboxImage = () => {
		setLightboxImageState('loading');
		setLightboxRetryKey((currentKey) => currentKey + 1);
	};

	const handleTouchStart = (event) => {
		const touch = event.changedTouches?.[0];

		if (!touch) {
			return;
		}

		touchStartRef.current = {
			x: touch.clientX,
			y: touch.clientY,
		};
	};

	const handleTouchEnd = (event) => {
		const touch = event.changedTouches?.[0];
		const start = touchStartRef.current;
		touchStartRef.current = null;

		if (!touch || !start || imageSlides.length < 2) {
			return;
		}

		const horizontalDistance = touch.clientX - start.x;
		const verticalDistance = touch.clientY - start.y;

		if (
			Math.abs(horizontalDistance) < SWIPE_THRESHOLD ||
			Math.abs(horizontalDistance) <= Math.abs(verticalDistance)
		) {
			return;
		}

		moveLightbox(horizontalDistance < 0 ? 1 : -1);
	};

	useEffect(() => {
		if (imageSlides.length === 0) {
			setSelectedIndex(0);
			setLightboxIndex(0);
			setLightboxOpen(false);
			return;
		}

		if (selectedIndex >= imageSlides.length) {
			setSelectedIndex(0);
		}

		if (lightboxIndex >= imageSlides.length) {
			setLightboxIndex(0);
		}
	}, [
		imageSlides.length,
		lightboxIndex,
		selectedIndex,
	]);

	useEffect(() => {
		const applyImageHash = () => {
			const match = window.location.hash.match(/^#img-(\d+)$/i);

			if (!match || imageSlides.length === 0) {
				return;
			}

			const requestedIndex = Number.parseInt(match[1], 10) - 1;

			if (
				Number.isNaN(requestedIndex) ||
				requestedIndex < 0 ||
				requestedIndex >= imageSlides.length
			) {
				return;
			}

			selectImage(requestedIndex);

			window.setTimeout(() => {
				galleryRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			}, 0);
		};

		applyImageHash();
		window.addEventListener('hashchange', applyImageHash);

		return () => {
			window.removeEventListener('hashchange', applyImageHash);
		};
	}, [
		imageSlides.length,
		selectImage,
	]);

	useEffect(() => {
		if (!lightboxOpen) {
			return undefined;
		}

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		window.setTimeout(() => {
			closeButtonRef.current?.focus();
		}, 0);

		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				closeLightbox();
				return;
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault();
				moveLightbox(1);
				return;
			}

			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				moveLightbox(-1);
				return;
			}

			if (event.key !== 'Tab' || !lightboxRef.current) {
				return;
			}

			const focusableElements = Array.from(
				lightboxRef.current.querySelectorAll(
					'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
				)
			).filter((element) => (
				!element.hasAttribute('hidden') &&
				element.getAttribute('aria-hidden') !== 'true'
			));

			if (focusableElements.length === 0) {
				event.preventDefault();
				return;
			}

			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];
			const activeElement = document.activeElement;

			if (
				event.shiftKey &&
				(
					activeElement === firstElement ||
					!lightboxRef.current.contains(activeElement)
				)
			) {
				event.preventDefault();
				lastElement.focus();
				return;
			}

			if (
				!event.shiftKey &&
				(
					activeElement === lastElement ||
					!lightboxRef.current.contains(activeElement)
				)
			) {
				event.preventDefault();
				firstElement.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.body.style.overflow = previousOverflow;
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [
		closeLightbox,
		lightboxOpen,
		moveLightbox,
	]);

	const copyReference = async () => {
		if (!lightboxSlide) {
			return;
		}

		const fileName = getFileName(lightboxSlide, lightboxIndex);
		const itemReference = hasText(referenceId)
			? String(referenceId).trim()
			: 'Object';
		const pageUrl = (
			`${window.location.origin}${window.location.pathname}${window.location.search}`
		);
		const deepLink = `${pageUrl}#img-${lightboxIndex + 1}`;
		const reference = (
			`${itemReference} — Image ${lightboxIndex + 1} of ${imageSlides.length} (${fileName})\n` +
			deepLink
		);

		try {
			await copyToClipboard(reference);
			setCopyState('copied');
		} catch (error) {
			console.error('Unable to copy image reference:', error);
			setCopyState('error');
		}
	};

	if (imageSlides.length === 0 || !selectedSlide) {
		return null;
	}

	const selectedFileName = getFileName(
		selectedSlide,
		selectedIndex
	);
	const selectedAlt = getImageAlt(
		selectedSlide,
		selectedIndex,
		objectTitle
	);
	const selectedThumbnailUrl = getThumbnailUrl(selectedSlide);
	const selectedFileSize = hasText(selectedSlide?.file_size)
		? String(selectedSlide.file_size).trim()
		: '';

	const lightboxFileName = lightboxSlide
		? getFileName(lightboxSlide, lightboxIndex)
		: '';
	const lightboxDownloadUrl = lightboxSlide
		? getDownloadUrl(lightboxSlide)
		: '';
	const lightboxFileSize = hasText(lightboxSlide?.file_size)
		? String(lightboxSlide.file_size).trim()
		: '';

	const lightbox = lightboxOpen && lightboxSlide
		? createPortal(
			<div
				ref={lightboxRef}
				className="fixed inset-0 z-[100] flex flex-col bg-black/90"
				role="dialog"
				aria-modal="true"
				aria-label="Image viewer"
				onMouseDown={(event) => {
					if (event.target === event.currentTarget) {
						closeLightbox();
					}
				}}
			>
				<div className="flex flex-wrap items-center gap-3 px-4 py-4 text-white sm:px-6">
					<div className="min-w-0 text-sm text-gray-300">
						Image{' '}
						<strong className="text-white">
							{lightboxIndex + 1}
						</strong>{' '}
						of{' '}
						<strong className="text-white">
							{imageSlides.length}
						</strong>
						<span className="mx-2 text-gray-500" aria-hidden="true">
							·
						</span>
						<span className="break-all">
							{lightboxFileName}
						</span>
						{lightboxFileSize && (
							<>
								<span className="mx-2 text-gray-500" aria-hidden="true">
									·
								</span>
								<span>{lightboxFileSize}</span>
							</>
						)}
					</div>

					<div className="ml-auto flex flex-wrap items-center gap-2">
						{lightboxDownloadUrl && (
							<a
								href={lightboxDownloadUrl}
								target="_blank"
								rel="noopener noreferrer"
								download
								className="inline-flex items-center gap-2 border border-gray-500 bg-transparent px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.8"
									stroke="currentColor"
									className="h-4 w-4"
									aria-hidden="true"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 3v12m0 0-4-4m4 4 4-4M5 21h14"
									/>
								</svg>
								Download image
							</a>
						)}

						<button
							type="button"
							onClick={copyReference}
							className={
								copyState === 'copied'
									? 'inline-flex items-center gap-2 bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-white'
									: 'inline-flex items-center gap-2 bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white'
							}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="1.8"
								stroke="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<rect
									x="9"
									y="9"
									width="11"
									height="11"
									rx="1"
								/>
								<path d="M5 15V5a1 1 0 0 1 1-1h10" />
							</svg>

							{copyState === 'copied'
								? 'Reference copied'
								: 'Copy reference'}
						</button>

						<button
							ref={closeButtonRef}
							type="button"
							onClick={closeLightbox}
							className="border border-gray-500 bg-transparent px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
						>
							Close
							<span className="ml-2" aria-hidden="true">✕</span>
						</button>
					</div>

					{copyState === 'error' && (
						<div
							className="w-full text-right text-sm text-red-300"
							role="status"
						>
							The reference could not be copied.
						</div>
					)}

					<div className="sr-only" aria-live="polite">
						{copyState === 'copied'
							? 'Image reference copied to clipboard.'
							: ''}
					</div>
				</div>

				<div
					className="relative flex min-h-0 flex-1 touch-pan-y items-center justify-center px-3 pb-4 sm:px-6"
					onMouseDown={(event) => {
						if (event.target === event.currentTarget) {
							closeLightbox();
						}
					}}
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}
				>
					{imageSlides.length > 1 && (
						<button
							type="button"
							onClick={() => moveLightbox(-1)}
							className="absolute left-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-3xl text-white hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-6 sm:h-14 sm:w-14"
							aria-label="Previous image"
						>
							<span aria-hidden="true">‹</span>
						</button>
					)}

					{lightboxImageState === 'loading' && (
						<div
							className="absolute inset-0 flex items-center justify-center"
							role="status"
							aria-live="polite"
						>
							<div className="flex flex-col items-center gap-3 text-white">
								<span
									className="h-11 w-11 animate-spin rounded-full border-4 border-white/30 border-t-white"
									aria-hidden="true"
								/>
								<span className="text-sm font-semibold">
									Loading image…
								</span>
							</div>
						</div>
					)}

					{lightboxImageState === 'error' && (
						<div
							className="relative z-10 mx-16 max-w-md border border-gray-600 bg-black/75 p-6 text-center text-white"
							role="alert"
						>
							<h3 className="text-lg font-bold">
								This image could not be loaded
							</h3>
							<p className="mt-2 text-sm text-gray-300">
								The original file may be temporarily unavailable.
							</p>
							<div className="mt-5 flex flex-wrap justify-center gap-3">
								<button
									type="button"
									onClick={retryLightboxImage}
									className="bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
								>
									Try again
								</button>

								{lightboxDownloadUrl && (
									<a
										href={lightboxDownloadUrl}
										target="_blank"
										rel="noopener noreferrer"
										download
										className="border border-gray-500 px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
									>
										Download original
									</a>
								)}
							</div>
						</div>
					)}

					<img
						key={`${lightboxSlide.src}-${lightboxRetryKey}`}
						src={lightboxSlide.src}
						alt={getImageAlt(
							lightboxSlide,
							lightboxIndex,
							objectTitle
						)}
						draggable="false"
						onLoad={() => setLightboxImageState('loaded')}
						onError={() => setLightboxImageState('error')}
						className={`max-h-full max-w-[calc(100%_-_6rem)] object-contain transition-opacity duration-150 sm:max-w-[calc(100%_-_9rem)] ${
							lightboxImageState === 'loaded'
								? 'opacity-100'
								: 'pointer-events-none opacity-0'
						}`}
					/>

					{imageSlides.length > 1 && (
						<button
							type="button"
							onClick={() => moveLightbox(1)}
							className="absolute right-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-3xl text-white hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-6 sm:h-14 sm:w-14"
							aria-label="Next image"
						>
							<span aria-hidden="true">›</span>
						</button>
					)}
				</div>

				<div className="px-4 pb-4 text-center text-sm text-gray-300 sm:px-6">
					{hasText(referenceId) && (
						<>
							{referenceId}
							<span className="mx-2 text-gray-500" aria-hidden="true">
								·
							</span>
						</>
					)}
					Image {lightboxIndex + 1} of {imageSlides.length}
					<span className="mx-2 text-gray-500" aria-hidden="true">
						·
					</span>
					<span className="break-all">
						{lightboxFileName}
					</span>
					{lightboxFileSize && (
						<>
							<span className="mx-2 text-gray-500" aria-hidden="true">
								·
							</span>
							<span>{lightboxFileSize}</span>
						</>
					)}
				</div>

				<div className="sr-only" aria-live="polite">
					Showing image {lightboxIndex + 1} of {imageSlides.length}: {lightboxFileName}
				</div>
			</div>,
			document.body
		)
		: null;

	return (
		<div ref={galleryRef}>
			<div
				className={
					imageSlides.length > 1
						? 'grid items-start gap-5 lg:grid-cols-[400px_minmax(0,1fr)]'
						: isHero
							? 'w-full max-w-[360px]'
							: 'max-w-[400px]'
				}
			>
				<div
					className={`relative mx-auto aspect-square w-full overflow-hidden border bg-[#f3efe7] ${
						isHero ? 'border-gray-700' : 'border-gray-200'
					}`}
				>
					{selectedThumbnailUrl && leadImageState !== 'error' && (
						<button
							type="button"
							onClick={() => openLightbox(selectedIndex)}
							className={`group absolute inset-0 block h-full w-full text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-inset ${
								isHero ? 'focus-visible:ring-white' : 'focus-visible:ring-black'
							}`}
							aria-label={`View the full-size version of image ${selectedIndex + 1} of ${imageSlides.length}: ${selectedFileName}`}
						>
							{leadImageState === 'loading' && (
								<div
									className="absolute inset-0 z-10 flex items-center justify-center bg-[#f3efe7]"
									role="status"
									aria-live="polite"
								>
									<div className="flex flex-col items-center gap-3 text-gray-800">
										<span
											className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-black"
											aria-hidden="true"
										/>
										<span className="text-sm font-semibold">
											Loading preview…
										</span>
									</div>
								</div>
							)}

							<img
								key={`${selectedThumbnailUrl}-${leadRetryKey}`}
								src={selectedThumbnailUrl}
								alt={selectedAlt}
								draggable="false"
								onLoad={() => setLeadImageState('loaded')}
								onError={() => setLeadImageState('error')}
								className={`h-full w-full object-contain transition-opacity duration-150 ${
									leadImageState === 'loaded'
										? 'opacity-100'
										: 'opacity-0'
								}`}
							/>

							<span
								className={`absolute right-3 top-3 z-20 rounded bg-black/80 px-3 py-1.5 text-xs font-semibold text-white ${
									isHero ? 'opacity-90' : ''
								}`}
							>
								View full-size
								{selectedFileSize ? ` · ${selectedFileSize}` : ''}
							</span>

							{!isHero && (
								<div className="absolute inset-x-0 bottom-0 z-20 flex items-end justify-between gap-4 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-4 pb-3 pt-12 text-white">
									<span className="min-w-0 truncate text-sm font-semibold">
										{selectedFileName}
									</span>
									<span className="shrink-0 rounded bg-black/75 px-2 py-1 text-xs font-bold">
										{selectedIndex + 1} / {imageSlides.length}
									</span>
								</div>
							)}
						</button>
					)}

					{(!selectedThumbnailUrl || leadImageState === 'error') && (
						<div
							className="absolute inset-0 flex items-center justify-center p-6 text-center"
							role={leadImageState === 'error' ? 'alert' : undefined}
						>
							<div className="max-w-sm">
								<h3 className="text-lg font-bold text-gray-900">
									{leadImageState === 'error'
										? 'This preview could not be loaded'
										: 'Preview unavailable'}
								</h3>
								<p className="mt-2 text-sm text-gray-600">
									The full-size image is still available on request.
								</p>
								<div className="mt-5 flex flex-wrap justify-center gap-3">
									{leadImageState === 'error' && (
										<button
											type="button"
											onClick={retryLeadImage}
											className="border border-gray-400 bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
										>
											Try preview again
										</button>
									)}

									<button
										type="button"
										onClick={() => openLightbox(selectedIndex)}
										className="bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
									>
										View full-size
										{selectedFileSize ? ` · ${selectedFileSize}` : ''}
									</button>
								</div>
							</div>
						</div>
					)}
				</div>

				{imageSlides.length > 1 && (
					<div>
						<div className="grid grid-cols-3 gap-2.5">
							{displayedSlides.map((slide, index) => {
								const fileName = getFileName(slide, index);
								const thumbnailUrl = getThumbnailUrl(slide);
								const isSelected = index === selectedIndex;

								return (
									<button
										id={`img-${index + 1}`}
										key={slide.id || `${slide.src}-${index}`}
										type="button"
										onClick={() => {
											selectImage(index);
											updateImageHash(index);
										}}
										className={
											isSelected
												? 'relative aspect-square w-full overflow-hidden border border-gray-300 bg-[#f3efe7] outline outline-[3px] outline-offset-[-3px] outline-black focus:outline-none focus-visible:ring-4 focus-visible:ring-black'
												: 'relative aspect-square w-full overflow-hidden border border-gray-200 bg-[#f3efe7] hover:border-gray-500 focus:outline-none focus-visible:ring-4 focus-visible:ring-black'
										}
										aria-current={isSelected ? 'true' : undefined}
										aria-label={`Show preview ${index + 1} of ${imageSlides.length}: ${fileName}`}
									>
										{thumbnailUrl ? (
											<img
												src={thumbnailUrl}
												alt={getImageAlt(
													slide,
													index,
													objectTitle
												)}
												draggable="false"
												className="h-full w-full object-cover"
												loading="lazy"
											/>
										) : (
											<span className="absolute inset-0 flex items-center justify-center p-2 text-center text-xs font-semibold text-gray-600">
												Preview unavailable
											</span>
										)}

										<span className="absolute left-1.5 top-1.5 rounded bg-black/80 px-1.5 py-0.5 text-[11px] font-bold text-white">
											{index + 1}
										</span>

										<span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/90 via-black/65 to-transparent px-1.5 pb-1.5 pt-5 text-left text-[11px] font-medium leading-tight text-white">
											{fileName}
										</span>
									</button>
								);
							})}
						</div>

						{imageSlides.length > THUMBNAIL_CAP && (
							<button
								type="button"
								onClick={() => setShowAll((current) => !current)}
								className="mt-3 border border-gray-300 bg-transparent px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
								aria-expanded={showAll}
							>
								{showAll
									? 'Show fewer images'
									: `Show all ${imageSlides.length} images`}
							</button>
						)}
					</div>
				)}
			</div>

			{lightbox}
		</div>
	);
};

export default MyGallery;
